import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Save, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { KBArticle, KBArticleInsert, generateSlug, useCreateArticle, useUpdateArticle, useDeleteArticle, usePlaybooks } from '@/hooks/useKnowledgeBase';

interface KBEditorProps {
  article?: KBArticle;
  mode: 'create' | 'edit';
}

const CATEGORIES = [
  { value: 'playbook', label: 'Playbook (Container)' },
  { value: 'guide', label: 'Guia (Documento Solto)' },
  { value: 'reference', label: 'Referência' },
];

const ICONS = [
  { value: 'book-open', label: '📚 Book Open' },
  { value: 'clipboard-list', label: '📋 Clipboard List' },
  { value: 'book-marked', label: '📖 Book Marked' },
  { value: 'folder-open', label: '📁 Folder Open' },
  { value: 'file-text', label: '📄 File Text' },
  { value: 'refresh-cw', label: '🔄 Refresh' },
  { value: 'users', label: '👥 Users' },
  { value: 'phone', label: '📞 Phone' },
  { value: 'video', label: '🎥 Video' },
  { value: 'calendar', label: '📅 Calendar' },
];

export function KBEditor({ article, mode }: KBEditorProps) {
  const navigate = useNavigate();
  const createArticle = useCreateArticle();
  const updateArticle = useUpdateArticle();
  const deleteArticle = useDeleteArticle();
  const { data: playbooks = [] } = usePlaybooks();

  const [title, setTitle] = useState(article?.title || '');
  const [slug, setSlug] = useState(article?.slug || '');
  const [content, setContent] = useState(article?.content || '');
  const [category, setCategory] = useState(article?.category || 'guide');
  const [parentId, setParentId] = useState<string | null>(article?.parent_id || null);
  const [icon, setIcon] = useState(article?.icon || 'file-text');
  const [isPublished, setIsPublished] = useState(article?.is_published ?? true);
  const [sortOrder, setSortOrder] = useState(article?.sort_order || 0);
  const [previewTab, setPreviewTab] = useState<'edit' | 'preview'>('edit');

  // Auto-generate slug from title
  useEffect(() => {
    if (mode === 'create' && title) {
      setSlug(generateSlug(title));
    }
  }, [title, mode]);

  // Reset parent when category changes to playbook
  useEffect(() => {
    if (category === 'playbook') {
      setParentId(null);
    }
  }, [category]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    if (!slug.trim()) {
      toast.error('Slug é obrigatório');
      return;
    }
    if (!category) {
      toast.error('Categoria é obrigatória');
      return;
    }

    try {
      if (mode === 'create') {
        const data: KBArticleInsert = {
          title: title.trim(),
          slug: slug.trim(),
          content,
          category,
          parent_id: parentId,
          icon,
          is_published: isPublished,
          sort_order: sortOrder,
        };
        await createArticle.mutateAsync(data);
        toast.success('Artigo criado com sucesso');
        navigate(`/knowledge-base/${slug}`);
      } else if (article) {
        await updateArticle.mutateAsync({
          id: article.id,
          title: title.trim(),
          slug: slug.trim(),
          content,
          category,
          parent_id: parentId,
          icon,
          is_published: isPublished,
          sort_order: sortOrder,
        });
        toast.success('Artigo atualizado com sucesso');
        navigate(`/knowledge-base/${slug}`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar artigo');
    }
  };

  const handleDelete = async () => {
    if (!article) return;
    
    try {
      await deleteArticle.mutateAsync(article.id);
      toast.success('Artigo excluído com sucesso');
      navigate('/knowledge-base');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao excluir artigo');
    }
  };

  const isLoading = createArticle.isPending || updateArticle.isPending || deleteArticle.isPending;

  // Filter playbooks to exclude self (for edit mode)
  const availablePlaybooks = playbooks.filter((p) => p.id !== article?.id);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/knowledge-base')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">
            {mode === 'create' ? 'Novo Artigo' : 'Editar Artigo'}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {mode === 'edit' && article && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isLoading}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir artigo?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação não pode ser desfeita. O artigo será permanentemente excluído.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            Salvar
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex min-h-0">
        {/* Settings Panel */}
        <div className="w-72 border-r border-border p-4 space-y-4 overflow-y-auto">
          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Primeiro Contato com Cliente"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug (URL)</Label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="primeiro-contato"
            />
          </div>

          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {category === 'playbook' 
                ? 'Playbooks são containers que agrupam vários artigos relacionados.'
                : category === 'guide'
                ? 'Guias são documentos soltos de referência.'
                : 'Documentos de referência técnica.'}
            </p>
          </div>

          {/* Parent Playbook selector - only show if not a playbook itself */}
          {category !== 'playbook' && availablePlaybooks.length > 0 && (
            <div className="space-y-2">
              <Label>Playbook Pai (opcional)</Label>
              <Select 
                value={parentId || 'none'} 
                onValueChange={(v) => setParentId(v === 'none' ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nenhum (documento solto)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum (documento solto)</SelectItem>
                  {availablePlaybooks.map((playbook) => (
                    <SelectItem key={playbook.id} value={playbook.id}>
                      📚 {playbook.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Vincular a um Playbook para organização hierárquica.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Ícone</Label>
            <Select value={icon} onValueChange={setIcon}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ICONS.map((ic) => (
                  <SelectItem key={ic.value} value={ic.value}>
                    {ic.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sort_order">Ordem de exibição</Label>
            <Input
              id="sort_order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
            />
            <p className="text-xs text-muted-foreground">
              Menor número = aparece primeiro
            </p>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="published">Publicado</Label>
            <Switch
              id="published"
              checked={isPublished}
              onCheckedChange={setIsPublished}
            />
          </div>
        </div>

        {/* Editor / Preview */}
        <div className="flex-1 flex flex-col min-h-0">
          <Tabs value={previewTab} onValueChange={(v) => setPreviewTab(v as 'edit' | 'preview')} className="flex-1 flex flex-col min-h-0">
            <div className="border-b border-border px-4">
              <TabsList className="h-10">
                <TabsTrigger value="edit" className="gap-2">
                  <EyeOff className="h-4 w-4" />
                  Editar
                </TabsTrigger>
                <TabsTrigger value="preview" className="gap-2">
                  <Eye className="h-4 w-4" />
                  Preview
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="edit" className="flex-1 m-0 min-h-0">
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Escreva o conteúdo do artigo em Markdown..."
                className="flex-1 h-full resize-none rounded-none border-0 focus-visible:ring-0 font-mono text-sm"
              />
            </TabsContent>

            <TabsContent value="preview" className="flex-1 m-0 min-h-0">
              <ScrollArea className="h-full">
                <div className="p-6 max-w-4xl">
                  <article className="prose prose-slate dark:prose-invert max-w-none prose-headings:scroll-mt-20 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-pre:bg-muted prose-pre:text-foreground prose-code:text-primary prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-table:border prose-th:bg-muted prose-th:p-2 prose-td:p-2 prose-td:border">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {content || '*Nenhum conteúdo para visualizar*'}
                    </ReactMarkdown>
                  </article>
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
